import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import API from "../api";

// 🟢 NEW: Haptic Feedback Engine
const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

const Checkout = ({ cart, setCart }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  // 🟢 PROMO CODE STATES
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Synchronous State Initialization
  const [localCart, setLocalCart] = useState(() => {
    if (cart && cart.length > 0) return cart;
    try {
      const savedCart = localStorage.getItem("cart");
      if (savedCart) return JSON.parse(savedCart);
    } catch (e) {
      console.error(e);
    }
    return [];
  });

  // Sync back to parent
  useEffect(() => {
    if (cart.length === 0 && localCart.length > 0) {
      setCart(localCart);
    }
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error("User parse error", e);
      }
    }
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    pincode: ""
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        phone: user.phone || prev.phone
      }));
    }
  }, [user]);

  const ADMIN_WHATSAPP_NUMBER = "918957199050"; 

  // CALCULATIONS 
  const totalMRP = localCart.reduce((sum, item) => sum + (item.mrp || item.price) * item.quantity, 0);
  const totalSellingPrice = localCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  let totalSavings = totalMRP - totalSellingPrice;

  // Delivery Fee Logic
  const deliveryFee = totalSellingPrice >= 150 ? 0 : 10;
  
  // 🟢 PROMO LOGIC CALCULATIONS
  let promoDiscountAmount = 0;
  if (appliedPromo) {
    if (appliedPromo.type === "FLAT") {
      promoDiscountAmount = appliedPromo.value;
    } else if (appliedPromo.type === "FREE_DELIVERY") {
      promoDiscountAmount = deliveryFee;
    } else if (appliedPromo.type === "PERCENTAGE") {
      promoDiscountAmount = Math.round((totalSellingPrice * appliedPromo.value) / 100);
    }
  }

  const finalPayableAmount = Math.max(0, totalSellingPrice + deliveryFee - promoDiscountAmount);
  totalSavings += promoDiscountAmount; // Add promo to total savings for the UI UI

  // 🟢 LIVE PROMO VALIDATION WITH BACKEND
  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoInput.trim()) return;

    try {
      setPromoLoading(true);
      
      // Sending the cartTotal so backend can check 'minOrderAmount'
      const { data } = await API.post("/promo/validate", {
        code: promoInput,
        cartTotal: totalSellingPrice
      });

      setAppliedPromo(data.promo);
      vibrate([50, 50, 50]); // Success burst
      toast.success(data.message || "Promo Code Applied!");
      setPromoInput("");
      
    } catch (err) {
      vibrate([100, 50, 100]); // Error double tap
      toast.error(err.response?.data?.message || "Invalid or expired Promo Code");
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    vibrate(30);
    setAppliedPromo(null);
    toast.success("Promo code removed");
  };

  // QUANTITY LOGIC (With Haptics)
  const updateQuantity = (productId, change) => {
    const updatedCart = localCart.map(item => {
      if (item._id === productId) {
        const newQty = item.quantity + change;
        if (change > 0 && item.stock && newQty > item.stock) {
          vibrate([100, 50, 100]); // 📳 Error double-vibrate
          toast.error(`Only ${item.stock} left in stock!`);
          return item;
        }
        return { ...item, quantity: Math.max(0, newQty) };
      }
      return item;
    }).filter(item => item.quantity > 0);

    // 📳 Light tap for + or -
    if (change > 0) vibrate(50); 
    else vibrate(30); 

    setLocalCart(updatedCart);
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart)); 

    // 🟢 SAFETY FIX: If cart is modified, remove promo to prevent cheating minimum order rules
    if (appliedPromo) {
      setAppliedPromo(null);
      toast("Cart updated. Please re-apply your promo code.", { icon: '⚠️' });
    }
    
    if (updatedCart.length === 0) {
      navigate("/");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    if (localCart.length === 0) {
      vibrate([100, 50, 100]); // 📳 Error
      return toast.error("Cart is empty!");
    }

    let finalUserId = null;
    const storedUserData = localStorage.getItem("user");

    if (storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        finalUserId = parsedUser._id || parsedUser.userId || parsedUser.id;

        if (!finalUserId) {
          vibrate([100, 50, 100]); // 📳 Error
          alert("⚠️ Authentication Error: Your User ID is missing. Please Logout and Login again.");
          return; 
        }
      } catch (err) {
        console.error("JSON Error", err);
      }
    }

    if (!formData.name || !formData.phone || !formData.address) {
      vibrate([100, 50, 100]); // 📳 Error
      return toast.error("Please fill all details");
    }

    try {
      setLoading(true);

      const orderData = {
        user: finalUserId,
        name: formData.name,
        phone: formData.phone,
        address: `${formData.address}, ${formData.pincode}`,
        items: localCart.map(item => ({
          product: item._id, 
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          image: item.image
        })),
        total: finalPayableAmount,
        // Optional: Send promo details to backend if you want to track it
        appliedPromo: appliedPromo ? appliedPromo.code : null,
        status: "Pending"
      };

      const { data } = await API.post("/orders", orderData);
      
      let message = `*🛒 New Order Placed!* \n\n`;
      message += `*Order ID:* ${data._id.slice(-6)} \n`;
      message += `*Name:* ${formData.name} \n`;
      message += `*Phone:* ${formData.phone} \n`;
      message += `*Address:* ${formData.address}, ${formData.pincode} \n\n`;
      message += `*Items:* \n`;
      
      localCart.forEach(item => {
        message += `• ${item.name} x ${item.quantity} (₹${item.price * item.quantity}) \n`;
      });
      
      message += `\n*Item Total:* ₹${totalSellingPrice}`;
      if (deliveryFee > 0) message += `\n*Delivery Fee:* ₹${deliveryFee}`;
      
      // 🟢 ADD PROMO TO WHATSAPP MESSAGE
      if (appliedPromo) {
        message += `\n*Promo Applied:* ${appliedPromo.code} (-₹${promoDiscountAmount})`;
      }

      message += `\n*💰 Grand Total: ₹${finalPayableAmount}*`;

      vibrate([50, 50, 50]); // 📳 SUCCESS BURST! Celebration pattern

      toast.success("Order Placed Successfully!");
      setLocalCart([]);
      setCart([]); 
      localStorage.removeItem("cart"); 
      
      const whatsappUrl = `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");

      setTimeout(() => {
        navigate("/my-orders");
      }, 1000);

    } catch (err) {
      console.error("Order Error:", err);
      vibrate([100, 50, 100]); // 📳 Error
      toast.error("Failed to place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 font-sans">
      <Toaster position="top-center" />
      
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 h-fit">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Order Summary</h2>
          
          <div className="space-y-4 mb-4 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
            {localCart.map(item => (
              <div key={item._id} className="flex justify-between items-center border-b border-gray-50 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                     {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover"/> : "🛍️"}
                  </div>
                  <div>
                    <div className="font-bold text-gray-700 text-sm line-clamp-1">{item.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="text-xs text-gray-500">
                         ₹{item.price} x {item.quantity}
                      </div>
                      <div className="flex items-center bg-gray-100 rounded-md ml-2 border border-gray-200">
                        <button onClick={() => updateQuantity(item._id, -1)} className="w-6 h-6 flex items-center justify-center text-gray-600 font-bold hover:bg-gray-200 rounded-l-md transition-colors">-</button>
                        <button onClick={() => updateQuantity(item._id, 1)} className="w-6 h-6 flex items-center justify-center text-green-600 font-bold hover:bg-gray-200 rounded-r-md transition-colors">+</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-800">₹{item.price * item.quantity}</div>
                  {item.mrp && item.mrp > item.price && (
                    <div className="text-[10px] text-gray-400 line-through">₹{item.mrp * item.quantity}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 🟢 NEW: PROMO CODE UI */}
          <div className="mb-6 bg-gray-50 p-3 rounded-xl border border-gray-200">
            {appliedPromo ? (
              <div className="flex justify-between items-center bg-green-100 border border-green-300 p-3 rounded-lg border-dashed">
                <div>
                  <span className="text-green-800 font-extrabold text-sm uppercase">✓ {appliedPromo.code}</span>
                  <span className="text-green-700 text-xs block font-medium">Promo applied successfully</span>
                </div>
                <button onClick={removePromo} className="text-red-500 text-xs font-bold hover:underline">Remove</button>
              </div>
            ) : (
              <form onSubmit={handleApplyPromo} className="flex gap-2">
                <input 
                  type="text" 
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  placeholder="Enter Promo Code" 
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm outline-none focus:border-green-500 uppercase"
                />
                <button 
                  type="submit" 
                  disabled={promoLoading || !promoInput.trim()}
                  className={`bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors ${promoLoading || !promoInput.trim() ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-900"}`}
                >
                  {promoLoading ? "..." : "Apply"}
                </button>
              </form>
            )}
          </div>
          
          <div className="border-t pt-4 space-y-2">
             <div className="flex justify-between text-gray-500 text-sm">
               <span>Item Total</span>
               <span>₹{totalSellingPrice}</span>
             </div>
             
             {totalMRP > totalSellingPrice && (
               <div className="flex justify-between text-gray-500 text-sm">
                 <span>Product Discount</span>
                 <span className="text-green-600 font-bold">-₹{totalMRP - totalSellingPrice}</span>
               </div>
             )}

             {/* 🟢 NEW: PROMO DISCOUNT DISPLAY */}
             {appliedPromo && (
               <div className="flex justify-between text-green-600 text-sm font-bold animate-in fade-in zoom-in duration-300">
                 <span>Promo ({appliedPromo.code})</span>
                 <span>-₹{promoDiscountAmount}</span>
               </div>
             )}
             
             <div className="flex justify-between text-gray-500 text-sm">
               <span>Delivery Fee</span>
               <span className={deliveryFee === 0 ? "text-green-600 font-bold" : "text-gray-700 font-medium"}>
                 {deliveryFee === 0 ? "FREE" : `₹${deliveryFee}`}
               </span>
             </div>
             
             <div className="my-2 border-t border-dashed border-gray-200"></div>
             
             <div className="flex justify-between text-xl font-extrabold text-gray-900">
               <span>Total to Pay</span>
               <span>₹{finalPayableAmount}</span>
             </div>

             {deliveryFee > 0 && !appliedPromo && (
               <div className="mt-3 bg-red-50 text-red-600 text-xs font-bold text-center py-2 rounded-lg border border-red-100">
                 Add ₹{150 - totalSellingPrice} more items to get FREE Delivery!
               </div>
             )}

             {totalSavings > 0 && (
               <div className="mt-3 bg-green-50 text-green-700 text-xs font-bold text-center py-2 rounded-lg border border-green-200">
                 🎉 You are saving ₹{totalSavings} on this order!
               </div>
             )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Shipping Details</h2>
          <form onSubmit={handlePlaceOrder} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Full Name</label>
              <input 
                name="name" 
                value={formData.name} 
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-green-500 transition-all font-medium" 
                placeholder="John Doe" 
                required 
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Phone Number</label>
              <input 
                name="phone" 
                type="number" 
                value={formData.phone} 
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-green-500 transition-all font-medium" 
                placeholder="9876543210" 
                required 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Pincode</label>
                <input 
                  name="pincode" 
                  type="number" 
                  value={formData.pincode} 
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-green-500 transition-all font-medium" 
                  placeholder="226001" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">City</label>
                <input className="w-full p-3 bg-gray-100 rounded-xl border border-transparent text-gray-500 cursor-not-allowed font-bold" value="Lucknow" disabled />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Full Address</label>
              <textarea 
                name="address" 
                value={formData.address} 
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-green-500 transition-all h-24 resize-none font-medium" 
                placeholder="House No, Street, Landmark..." 
                required 
              ></textarea>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              onClick={() => {
                // Pre-click haptic feedback just so the button feels tactile when pressed
                if (!loading && localCart.length > 0) vibrate(20); 
              }}
              className={`w-full py-4 rounded-xl font-bold text-lg text-white shadow-lg transition-all transform active:scale-95 flex justify-between px-6 items-center ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
            >
              <span>{loading ? "Processing..." : `Place Order ➔`}</span>
              {!loading && <span>₹{finalPayableAmount}</span>}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default Checkout;